import { Platform } from 'react-native';

export type ConnectionStatus = 'idle' | 'connecting' | 'connected' | 'disconnected' | 'error';

export interface TranscriptEntry {
  speaker: 'agent' | 'lead';
  text: string;
  isFinal: boolean;
}

export interface ToolCall {
  id: string;
  name: string;
  args: unknown;
}

export interface ConvaiSessionOptions {
  signedWsUrl: string;
  onStatusChange: (status: ConnectionStatus) => void;
  onTranscript: (entry: TranscriptEntry) => void;
  onAudioChunk: (base64: string) => void;
  onToolCall: (call: ToolCall) => void;
  onError: (err: unknown) => void;
  /**
   * Optional async callback to mint a fresh ElevenLabs signed URL before
   * reconnecting. Without it, reconnects after the original URL's TTL
   * (shorter than the 20-min backend session) hit immediate auth failures.
   */
  refreshSignedUrl?: () => Promise<string>;
  /**
   * Dynamic variables to send in the WS init message.
   * ElevenLabs closes the connection with code 1008 if any variable
   * referenced in the agent's first message / prompt is missing.
   */
  dynamicVariables?: Record<string, string>;
}

const MAX_RECONNECT_ATTEMPTS = 4;
const RECONNECT_BASE_MS = 1000;

export class ConvaiSession {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private closed = false;
  private initSent = false;
  private readonly opts: ConvaiSessionOptions;
  private currentSignedUrl: string;

  constructor(opts: ConvaiSessionOptions) {
    this.opts = opts;
    this.currentSignedUrl = opts.signedWsUrl;
  }

  async connect(): Promise<void> {
    if (Platform.OS !== 'web') {
      this.opts.onError(new Error('ConvaiSession is only supported on web'));
      return;
    }

    this.closed = false;
    this.reconnectAttempts = 0;
    this.opts.onStatusChange('connecting');
    this.openSocket();
  }

  sendAudioChunk(base64Pcm16: string): void {
    this.send({ user_audio_chunk: base64Pcm16 });
  }

  sendToolResponse(params: { id: string; name: string; response: unknown }): void {
    this.send({
      type: 'client_tool_result',
      tool_call_id: params.id,
      result:
        typeof params.response === 'string' ? params.response : JSON.stringify(params.response),
      is_error: false,
    });
  }

  /**
   * True once the WebSocket has completed its handshake. Callers use this to
   * avoid treating a burst of mic input as a barge-in interrupt while a
   * reconnect (e.g. after resuming a preview session) is still in flight.
   */
  get isOpen(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * Sends the interrupt signal only when the socket is actually open.
   * Returns whether it fired so callers can gate local state (queue clear,
   * isPlaying) on the interrupt having actually reached the server instead
   * of assuming success during a reconnect window.
   */
  interrupt(): boolean {
    if (!this.isOpen) return false;
    this.send({ type: 'user_message', text: '[interrupt]' });
    return true;
  }

  close(): void {
    this.closed = true;
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.opts.onStatusChange('disconnected');
  }

  private send(message: unknown): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    }
  }

  private async openSocket(): Promise<void> {
    if (this.closed) return;

    try {
      // On reconnects, the original signed URL may have expired (ElevenLabs
      // URLs have a TTL shorter than the 20-min backend session). Try to mint
      // a fresh one via the optional callback; if unavailable, fall back to
      // the last known URL so behavior remains backwards compatible.
      if (this.reconnectAttempts > 0 && this.opts.refreshSignedUrl) {
        try {
          this.currentSignedUrl = await this.opts.refreshSignedUrl();
        } catch (err) {
          this.opts.onError(err);
          // continue with stale URL — better than aborting; will surface auth
          // error on connect which the user can act on.
        }
      }

      const ws = new WebSocket(this.currentSignedUrl);
      this.ws = ws;
      this.initSent = false;

      ws.onopen = () => {
        this.reconnectAttempts = 0;
        this.opts.onStatusChange('connected');
        if (!this.initSent) {
          this.send({
            type: 'conversation_initiation_client_data',
            dynamic_variables: this.opts.dynamicVariables ?? {},
          });
          this.initSent = true;
        }
      };

      ws.onmessage = (event: MessageEvent) => {
        if (this.closed) return;
        this.handleMessage(event.data as string);
      };

      ws.onerror = (event: Event) => {
        if (this.closed) return;
        this.opts.onError(event);
      };

      ws.onclose = (event: CloseEvent) => {
        if (this.closed) return;

        const reason = event.reason ? `, reason=${event.reason}` : '';
        const errorMessage = `WebSocket closed (code=${event.code}${reason})`;

        // Permanent protocol-level closes — do not reconnect; surface as error.
        // 1008: policy violation (e.g. missing required dynamic variables)
        // 1002: protocol error
        // 1003: unsupported data
        const isPermanent = event.code === 1008 || event.code === 1002 || event.code === 1003;

        if (isPermanent) {
          this.opts.onStatusChange('error');
          this.opts.onError(new Error(errorMessage));
          return;
        }

        this.opts.onError(new Error(errorMessage));
        this.opts.onStatusChange('disconnected');
        this.scheduleReconnect();
      };
    } catch (err) {
      this.opts.onError(err);
      this.opts.onStatusChange('error');
    }
  }

  private handleMessage(raw: string): void {
    try {
      const msg = JSON.parse(raw) as Record<string, unknown>;

      if (msg.type === 'ping') {
        const pingEvent = msg.ping_event as { event_id?: number } | undefined;
        if (pingEvent?.event_id !== undefined) {
          this.send({ type: 'pong', event_id: pingEvent.event_id });
        }
        return;
      }

      if (msg.type === 'conversation_initiation_metadata') {
        return;
      }

      if (msg.type === 'audio') {
        const audioEvent = msg.audio_event as { audio_base_64?: string } | undefined;
        const audio = audioEvent?.audio_base_64 ?? (msg.audio as string | undefined);
        if (audio) this.opts.onAudioChunk(audio);
        return;
      }

      if (msg.type === 'user_transcript') {
        const transcriptEvent = msg.user_transcription_event as
          | { user_transcript?: string }
          | undefined;
        this.opts.onTranscript({
          speaker: 'lead',
          text: transcriptEvent?.user_transcript ?? '',
          isFinal: true,
        });
        return;
      }

      if (msg.type === 'agent_response') {
        const agentEvent = msg.agent_response_event as { agent_response?: string } | undefined;
        this.opts.onTranscript({
          speaker: 'agent',
          text: agentEvent?.agent_response ?? '',
          isFinal: true,
        });
        return;
      }

      if (msg.type === 'agent_response_correction') {
        const corrEvent = msg.agent_response_correction_event as
          | { corrected_agent_response?: string }
          | undefined;
        if (corrEvent?.corrected_agent_response) {
          this.opts.onTranscript({
            speaker: 'agent',
            text: corrEvent.corrected_agent_response,
            isFinal: true,
          });
        }
        return;
      }

      if (msg.type === 'client_tool_call') {
        const toolEvent = msg.client_tool_call as
          | { tool_call_id?: string; tool_name?: string; parameters?: unknown }
          | undefined;
        if (toolEvent) {
          this.opts.onToolCall({
            id: toolEvent.tool_call_id ?? '',
            name: toolEvent.tool_name ?? '',
            args: toolEvent.parameters ?? {},
          });
        }
        return;
      }

      if (msg.type === 'transcript') {
        const speaker = msg.role === 'agent' || msg.speaker === 'agent' ? 'agent' : 'lead';
        this.opts.onTranscript({
          speaker,
          text: (msg.text as string) ?? '',
          isFinal: Boolean(msg.is_final ?? msg.isFinal ?? true),
        });
        return;
      }
    } catch (err) {
      this.opts.onError(err);
    }
  }

  private scheduleReconnect(): void {
    if (this.closed) return;
    if (this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      this.opts.onStatusChange('error');
      this.opts.onError(new Error(`Max reconnect attempts (${MAX_RECONNECT_ATTEMPTS}) exceeded`));
      return;
    }

    const delay = RECONNECT_BASE_MS * 2 ** this.reconnectAttempts;
    this.reconnectAttempts += 1;
    this.opts.onStatusChange('connecting');

    setTimeout(() => {
      this.openSocket();
    }, delay);
  }
}
